import React from 'react'
import moment from 'moment'
import ImageIcon from '@mui/icons-material/Image'
import VideocamIcon from '@mui/icons-material/Videocam'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import PhoneIcon from '@mui/icons-material/Phone'
import LaunchIcon from '@mui/icons-material/Launch'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DoneAllIcon from '@mui/icons-material/DoneAll'
import messagesLib from '../../libs/messages'

export function normalizeStatus(value) {
  if (value == null) return null
  const upper = String(value).toUpperCase().trim()
  if (!upper) return null
  return upper
}

export function findComponent(components, type) {
  if (!Array.isArray(components)) return null
  return components.find((c) => normalizeStatus(c?.type) === type) || null
}

// Realce "marca-texto" do que veio de variável preenchida (escolha do usuário).
// Vive dentro da bolha branca do WhatsApp, então cor fixa (não depende de tema).
const VAR_HIGHLIGHT_STYLE = {
  backgroundColor: '#d9fdd3',
  borderRadius: 3,
  padding: '0 3px',
  color: '#0b3d2e',
}

// Estilo do trecho dinâmico do link de um botão (ex.: código do link de pagamento).
const URL_VAR_STYLE = { color: '#00a884', fontWeight: 700 }

// Texto monoespaçado (markdown ` e ``` do WhatsApp).
const MONO_STYLE = {
  fontFamily: 'monospace',
  fontSize: '0.92em',
  backgroundColor: 'rgba(0,0,0,0.05)',
  borderRadius: 3,
  padding: '0 3px',
}

// Chip do placeholder {{n}} na visão "modelo" (sem valores) — mantém o visual
// antigo da Home quando o preview é só do template, sem dados enviados.
function PlaceholderChip({ children }) {
  return (
    <span
      className="inline-block px-1 mx-0.5 rounded text-[11px] font-bold"
      style={{ backgroundColor: '#e8e6df', color: '#075e54' }}
    >
      {children}
    </span>
  )
}

// Renderiza um pedaço de texto puro, destacando o que veio de variável. Sem
// valores, os {{n}} aparecem como chips (visão "modelo"); com valores, o trecho
// preenchido ganha o realce marca-texto.
function renderTextTokens(text, values, keyPrefix) {
  const tokens = messagesLib.tokenizeFilledText(text, values)
  return tokens.map((token, i) => {
    const key = `${keyPrefix}-${i}`
    if (token.kind === 'var') {
      return <mark key={key} style={VAR_HIGHLIGHT_STYLE}>{token.text}</mark>
    }
    if (/^\{\{\d+\}\}$/.test(token.text)) {
      return <PlaceholderChip key={key}>{token.text}</PlaceholderChip>
    }
    return <React.Fragment key={key}>{token.text}</React.Fragment>
  })
}

// Converte a AST do markdown do WhatsApp em nós React, aplicando o destaque de
// variável por cima dos nós de texto (assim *{{1}}* vira negrito + realce juntos).
function renderMarkdownNodes(nodes, values, keyPrefix) {
  return nodes.map((node, i) => {
    const key = `${keyPrefix}-${i}`
    if (node.type === 'text') {
      return <React.Fragment key={key}>{renderTextTokens(node.value, values, key)}</React.Fragment>
    }
    const children = renderMarkdownNodes(node.children, values, key)
    switch (node.type) {
      case 'bold': return <b key={key}>{children}</b>
      case 'italic': return <i key={key}>{children}</i>
      case 'strike': return <s key={key}>{children}</s>
      case 'code':
      case 'codeblock': return <code key={key} style={MONO_STYLE}>{children}</code>
      default: return <React.Fragment key={key}>{children}</React.Fragment>
    }
  })
}

// Renderiza o texto do template: markdown do WhatsApp + destaque das variáveis
// preenchidas. Sem `values`, mostra os placeholders {{n}} como chips (visão
// "modelo", usada na Home). Mantém a assinatura antiga.
export function renderTemplateText(text, values) {
  if (!text) return null
  const ast = messagesLib.parseWhatsappMarkdown(text)
  return renderMarkdownNodes(ast, values, 't')
}

const BUTTON_ICONS = {
  URL: LaunchIcon,
  PHONE_NUMBER: PhoneIcon,
  QUICK_REPLY: ContentCopyIcon,
  COPY_CODE: ContentCopyIcon,
}

// Último segmento do caminho de uma URL (ex.: o código do link de pagamento).
function lastPathSegment(url) {
  const clean = String(url || '').split('?')[0].split('#')[0]
  const seg = clean.replace(/\/+$/, '').split('/').pop()
  return seg || null
}

// Resolve o link real de um botão a partir da definição do template + o valor
// enviado (%ButtonUrl%) + o link de pagamento da mensagem. Devolve null quando o
// botão não leva a lugar nenhum (quick reply, copiar código) ou não resolveu de
// fato (sobrou {{...}}). `dynamic` é o trecho do link que veio de variável, pra
// destacar.
function resolveButtonLink(button, value, paymentLink) {
  const type = normalizeStatus(button?.type)
  const rawValue = String(value || '').trim()
  const isPayment =
    /PAYMENT_LINK/i.test(button?.url || '') ||
    /PAYMENT_LINK/i.test(rawValue) ||
    /PAYMENT_LINK/i.test(button?.example?.[0] || '')

  if (type === 'PHONE_NUMBER') {
    const phone = button?.phone_number || rawValue || button?.text
    if (!phone) return null
    return { prefix: 'liga para', href: `tel:${phone}`, display: String(phone), dynamic: null }
  }

  if (type === 'URL' || isPayment) {
    let href = ''
    let dynamic = null

    if (isPayment && paymentLink) {
      href = paymentLink
      dynamic = lastPathSegment(paymentLink)
    } else {
      const base = button?.url || button?.example?.[0] || ''
      if (/\{\{\d+\}\}/.test(base) && rawValue) {
        href = base.replace(/\{\{\d+\}\}/, rawValue)
        dynamic = rawValue
      } else if (/^https?:/i.test(rawValue)) {
        href = rawValue
      } else if (rawValue && base && base.endsWith('/')) {
        href = base + rawValue
        dynamic = rawValue
      } else {
        href = base
      }
    }

    if (!href || /\{\{.*?\}\}/.test(href)) return null
    return { prefix: 'abre', href, display: href, dynamic }
  }

  return null
}

// Mostra o link do botão com o trecho dinâmico realçado.
function renderLinkDisplay(display, dynamic) {
  if (!dynamic || !display.includes(dynamic)) return display
  const idx = display.indexOf(dynamic)
  return (
    <>
      {display.slice(0, idx)}
      <span style={URL_VAR_STYLE}>{dynamic}</span>
      {display.slice(idx + dynamic.length)}
    </>
  )
}

// Bolha estilo WhatsApp do template. `values` (opcional) preenche os {{n}} com
// os dados realmente enviados ao cliente. `buttonValues` (opcional, alinhado por
// índice aos botões) e `paymentLink` (opcional) habilitam o link real clicável
// embaixo de cada botão.
export function TemplatePreviewBubble({ template, values, buttonValues = [], paymentLink }) {
  const components = template?.components || []
  const header = findComponent(components, 'HEADER')
  const body = findComponent(components, 'BODY')
  const footer = findComponent(components, 'FOOTER')
  const buttonsComponent = findComponent(components, 'BUTTONS')
  const buttons = buttonsComponent?.buttons || []

  const headerFormat = normalizeStatus(header?.format)
  const headerIsMedia = headerFormat && headerFormat !== 'TEXT'

  return (
    <div
      className="rounded-lg p-3 max-w-[340px] w-full shadow-md text-sm"
      style={{
        backgroundColor: '#ffffff',
        color: '#0b141a',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif',
        position: 'relative',
      }}
    >
      {/* Triângulo lateral estilo WhatsApp */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: -8,
          width: 0,
          height: 0,
          borderTop: '0 solid transparent',
          borderBottom: '12px solid transparent',
          borderRight: '10px solid #ffffff',
        }}
      />

      {header && headerIsMedia && (
        <div
          className="flex items-center justify-center mb-2 rounded-md"
          style={{ backgroundColor: '#e9edef', minHeight: 120 }}
        >
          {headerFormat === 'IMAGE' && <ImageIcon style={{ fontSize: 48, color: '#8696a0' }} />}
          {headerFormat === 'VIDEO' && <VideocamIcon style={{ fontSize: 48, color: '#8696a0' }} />}
          {headerFormat === 'DOCUMENT' && <InsertDriveFileIcon style={{ fontSize: 48, color: '#8696a0' }} />}
        </div>
      )}

      {header && headerFormat === 'TEXT' && (
        <div className="font-bold text-[15px] mb-2 leading-snug" style={{ color: '#0b141a' }}>
          {renderTemplateText(header.text, values)}
        </div>
      )}

      {body?.text && (
        <div className="whitespace-pre-wrap text-[14px] leading-snug" style={{ color: '#0b141a' }}>
          {renderTemplateText(body.text, values)}
        </div>
      )}

      {footer?.text && (
        <div className="mt-2 text-[12px] font-light" style={{ color: '#667781' }}>
          {footer.text}
        </div>
      )}

      <div className="flex items-center justify-end gap-1 mt-1.5 text-[11px]" style={{ color: '#667781' }}>
        <span>{moment().format('HH:mm')}</span>
        <DoneAllIcon style={{ fontSize: 14, color: '#53bdeb' }} />
      </div>

      {buttons.length > 0 && (
        <div className="mt-2 pt-2 -mx-3 -mb-3 rounded-b-lg" style={{ borderTop: '1px solid #e9edef' }}>
          {buttons.map((b, idx) => {
            const btnType = normalizeStatus(b?.type)
            const Icon = BUTTON_ICONS[btnType] || LaunchIcon
            const label = b.text || b.example?.[0] || (btnType === 'COPY_CODE' ? 'Copiar código' : '—')
            const link = resolveButtonLink(b, buttonValues[idx], paymentLink)
            return (
              <div
                key={idx}
                className="py-2 px-1"
                style={{ borderTop: idx > 0 ? '1px solid #e9edef' : 'none' }}
              >
                <div
                  className="flex items-center justify-center gap-1.5 text-[14px] font-medium"
                  style={{ color: '#00a884' }}
                >
                  <Icon style={{ fontSize: 16 }} />
                  <span>{label}</span>
                </div>
                {link && (
                  <a
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="block text-center text-[10.5px] mt-1 break-all hover:underline"
                    style={{ color: '#5b6b75' }}
                  >
                    {link.prefix}: {renderLinkDisplay(link.display, link.dynamic)}
                  </a>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default TemplatePreviewBubble
